<script setup>
import { h, reactive, ref, unref, computed, watch } from "vue";
import { ReloadOutlined, SearchOutlined } from "@ant-design/icons-vue";
import { getRequest } from "@/utils/request.js";
import { Table } from "ant-design-vue";

const emit = defineEmits(["ok"]);
const title = "选择关键字";
const open = ref(false);

const formRef = ref();
const searchFormModel = ref({});
const datasource = ref([]);
const loading = ref(false);
const columns = [
  {
    key: "title",
    dataIndex: "title",
    title: "偏好标题",
    width: "30%",
  },
  {
    key: "categoryName",
    dataIndex: "categoryName",
    title: "所属分类",
    width: "20%",
  },
  {
    key: "multipleChoice",
    dataIndex: "multipleChoice",
    title: "多选/单选",
    width: "10%",
  },
  {
    key: "options",
    dataIndex: "options",
    title: "选项",
    width: "30%",
    customRender: ({ text, record, index, column }) => {
      if (Array.isArray(text)) {
        return text.map((item) => item.name).join(",");
      }
    },
  },
];

const pagination = reactive({
  current: 1,
  pageSize: 10,
  pageSizeOptions: ["10", "50", "100"],
  showTotal: (total, range) => {
    return range[0] + "-" + range[1] + " 共" + total + "条";
  },
  showQuickJumper: true,
  showSizeChanger: true,
  total: 0,
});

const loadData = async () => {
  loading.value = true;
  const { current, pageSize } = unref(pagination);
  const requestBody = Object.assign({}, unref(searchFormModel), {
    pageNo: current,
    pageSize,
  });
  const response = await getRequest("/jobTypePreference", requestBody).finally(
    () => {
      loading.value = false;
    },
  );
  if (response.success) {
    datasource.value = response.data.records;
    pagination.total = response.data.total;
  }
};

const resetForm = () => {
  formRef.value.resetFields();
  loadData();
};

const tableChange = ({ current, pageSize }) => {
  pagination.current = current;
  pagination.pageSize = pageSize;
  loadData();
};

const handleClose = () => {
  open.value = false;
};

const handleConfirm = () => {
  handleClose();
  emit("ok", selectedIds.value);
};

const category = ref([]);
const loadCategory = () => {
  getRequest("/jobTypePreferenceCategory/tree").then((response) => {
    if (response.success) {
      category.value = response.data;
    }
  });
};
const selectedIds = ref([]);
const selectedItems = ref([]);
const handleShow = async (preSelectedIds) => {
  open.value = true;
  if (Array.isArray(preSelectedIds)) {
    selectedIds.value = preSelectedIds;
  }
  loadCategory();
  await loadData();
  const data = unref(datasource);
  if (data && data.length) {
    selectedItems.value = data.filter((item) => {
      return selectedIds.value.includes(item.id);
    });
  }
};

watch(
  () => selectedIds,
  () => {
    const data = unref(datasource);
    if (data && data.length) {
      selectedItems.value = data.filter((item) => {
        return selectedIds.value.includes(item.id);
      });
    }
  },
  { immediate: false, deep: true },
);

const onSelectChange = (changableRowKeys) => {
  console.log("selectedRowKeys changed: ", changableRowKeys);
  selectedIds.value = changableRowKeys;
};

const rowSelection = computed(() => {
  return {
    fixed: true,
    preserveSelectedRowKeys: true,
    selectedRowKeys: unref(selectedIds),
    onChange: onSelectChange,
    hideDefaultSelections: true,
  };
});

const rowClick = (record, index) => {
  return {
    onClick: () => {
      console.info(record, index);
      if (selectedIds.value.includes(record.id)) {
        const index = selectedIds.value.findIndex((id) => id === record.id);
        selectedIds.value.splice(index, 1);
      } else {
        selectedIds.value.push(record.id);
      }
    },
  };
};

defineExpose({
  handleShow,
});
</script>

<template>
  <a-modal
    width="1200px"
    :open="open"
    :title="title"
    @cancel="handleClose"
    @ok="handleConfirm"
  >
    <a-card>
      <a-form
        ref="formRef"
        layout="inline"
        :labelCol="{ span: 3, offset: 1 }"
        :model="searchFormModel"
        @keydown.enter="loadData"
      >
        <a-row :gutter="[10, 10]" style="width: 100%">
          <a-col :span="12">
            <a-form-item label="标题" name="title">
              <a-input
                v-model:value="searchFormModel.title"
                placeholder="请输入标题"
              />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="分组" name="title">
              <a-tree-select
                v-model:value="searchFormModel.categoryId"
                placeholder="请选择分组"
                :tree-data="category"
                :field-names="{
                  children: 'children',
                  label: 'name',
                  value: 'id',
                }"
              />
            </a-form-item>
          </a-col>
          <a-col :span="24">
            <a-button
              type="primary"
              style="margin-left: 18px"
              :icon="h(SearchOutlined)"
              @click="loadData"
              @keydown.enter="loadData"
            >
              查询
            </a-button>
            <a-button
              style="margin-left: 18px"
              :icon="h(ReloadOutlined)"
              @click="resetForm"
              >重置
            </a-button>
          </a-col>
        </a-row>
      </a-form>
    </a-card>
    <a-card style="margin-top: 10px; background-color: rgba(22, 119, 255, 0.1)">
      已选择：{{ selectedItems.map((item) => item.title).join("，") }}
    </a-card>
    <a-card style="margin-top: 10px">
      <a-table
        row-key="id"
        :loading="loading"
        :columns="columns"
        :data-source="datasource"
        :bordered="true"
        :pagination="pagination"
        :row-selection="rowSelection"
        :customRow="rowClick"
        @change="tableChange"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.dataIndex === 'multipleChoice'">
            <template v-if="record[column.dataIndex]">
              <a-tag color="orange">多选</a-tag>
            </template>
            <template v-else>
              <a-tag color="blue">单选</a-tag>
            </template>
          </template>
        </template>
      </a-table>
    </a-card>
  </a-modal>
</template>

<style scoped lang="scss"></style>
